const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

const base64ToBytes = base64 => {
  const cleanBase64 = base64.includes(',') ? base64.split(',').pop() : base64;
  const byteCharacters = atob(cleanBase64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let index = 0; index < byteCharacters.length; index += 1) {
    byteNumbers[index] = byteCharacters.charCodeAt(index);
  }

  return new Uint8Array(byteNumbers);
};

const getImageExtension = bytes => {
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'jpg';
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return 'png';
  }
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'gif';
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'webp';
  }

  return null;
};

const getDownloadFileName = (file, index, bytes) => {
  const fallbackName = `inspection-image-${index + 1}`;
  const name = (file.filename || file.path?.split('/').pop() || fallbackName).replace(/[\\/]/g, '-');
  const imageExtension = getImageExtension(bytes);
  const currentExtension = name.includes('.') ? name.split('.').pop().toLowerCase() : null;

  if (!imageExtension) {
    return name;
  }

  if (currentExtension && IMAGE_EXTENSIONS.includes(currentExtension)) {
    return name;
  }

  return `${name.replace(/\.[^.]+$/, '')}.${imageExtension}`;
};

const getImageMimeType = filename => {
  const extension = filename?.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    default:
      return 'application/octet-stream';
  }
};

const getCrcTable = () => {
  const table = [];

  for (let index = 0; index < 256; index += 1) {
    let crc = index;

    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }

    table[index] = crc >>> 0;
  }

  return table;
};

const getCrc32 = bytes => {
  const table = getCrcTable();
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc = table[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const getLittleEndianBytes = (value, length) => {
  const bytes = [];

  for (let index = 0; index < length; index += 1) {
    bytes.push((value >>> (index * 8)) & 0xff);
  }

  return bytes;
};

const createZipBlob = files => {
  const encoder = new TextEncoder();
  const now = new Date();
  const zipTime =
    (now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2);
  const zipDate =
    ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(file => {
    const filename = encoder.encode(file.filename);
    const crc = getCrc32(file.bytes);
    const localHeader = new Uint8Array([
      ...getLittleEndianBytes(0x04034b50, 4),
      ...getLittleEndianBytes(20, 2),
      ...getLittleEndianBytes(0x0800, 2),
      ...getLittleEndianBytes(0, 2),
      ...getLittleEndianBytes(zipTime, 2),
      ...getLittleEndianBytes(zipDate, 2),
      ...getLittleEndianBytes(crc, 4),
      ...getLittleEndianBytes(file.bytes.length, 4),
      ...getLittleEndianBytes(file.bytes.length, 4),
      ...getLittleEndianBytes(filename.length, 2),
      ...getLittleEndianBytes(0, 2)
    ]);
    const centralHeader = new Uint8Array([
      ...getLittleEndianBytes(0x02014b50, 4),
      ...getLittleEndianBytes(20, 2),
      ...getLittleEndianBytes(20, 2),
      ...getLittleEndianBytes(0x0800, 2),
      ...getLittleEndianBytes(0, 2),
      ...getLittleEndianBytes(zipTime, 2),
      ...getLittleEndianBytes(zipDate, 2),
      ...getLittleEndianBytes(crc, 4),
      ...getLittleEndianBytes(file.bytes.length, 4),
      ...getLittleEndianBytes(file.bytes.length, 4),
      ...getLittleEndianBytes(filename.length, 2),
      ...getLittleEndianBytes(0, 2),
      ...getLittleEndianBytes(0, 2),
      ...getLittleEndianBytes(0, 2),
      ...getLittleEndianBytes(0, 2),
      ...getLittleEndianBytes(0, 4),
      ...getLittleEndianBytes(offset, 4)
    ]);

    localParts.push(localHeader, filename, file.bytes);
    centralParts.push(centralHeader, filename);
    offset += localHeader.length + filename.length + file.bytes.length;
  });

  const centralDirectorySize = centralParts.reduce((total, part) => total + part.length, 0);
  const endRecord = new Uint8Array([
    ...getLittleEndianBytes(0x06054b50, 4),
    ...getLittleEndianBytes(0, 2),
    ...getLittleEndianBytes(0, 2),
    ...getLittleEndianBytes(files.length, 2),
    ...getLittleEndianBytes(files.length, 2),
    ...getLittleEndianBytes(centralDirectorySize, 4),
    ...getLittleEndianBytes(offset, 4),
    ...getLittleEndianBytes(0, 2)
  ]);

  return new Blob([...localParts, ...centralParts, endRecord], { type: 'application/zip' });
};

const downloadBlob = (blob, filename) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement('a'), {
    href: blobUrl,
    download: filename
  });

  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
};

const downloadInspectionImageFiles = (files, inspectionId) => {
  const downloadFiles = files.map((file, index) => {
    const bytes = base64ToBytes(file.base64);

    return {
      bytes,
      filename: getDownloadFileName(file, index, bytes)
    };
  });

  if (downloadFiles.length === 1) {
    const [file] = downloadFiles;

    downloadBlob(new Blob([file.bytes], { type: getImageMimeType(file.filename) }), file.filename);
    return;
  }

  downloadBlob(createZipBlob(downloadFiles), `inspection-${inspectionId}-pictures.zip`);
};

const getInspectionImageFormItems = (files = []) => {
  return files
    .map(file => ({
      file: file.base64?.includes(',') ? file.base64.split(',').pop() : file.base64
    }))
    .filter(image => image.file);
};

export { downloadInspectionImageFiles, getInspectionImageFormItems };
